import { ReactComponent as FilterIcon } from "../../assets/icons/filters.svg";
import { ReactComponent as DotsIcon } from "../../assets/icons/more.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as ArrowLeftIcon } from "../../assets/icons/chevron-left.svg";
import UserIcon from "../../assets/icons/profile-icon.svg";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SubscriptionBadge from "../../components/SubscriptionBadge";
import UIPopover from "../../components/Popover";
// import UserDetails, { ConfirmBlock, ConfirmDelete } from "./UserDetails";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../helpers/api";
import moment from "moment";
import { ShowToast } from "../../components/toast";
import clsx from "clsx";
import { Button } from "../../components/Button";
import SearchComponent from "../../components/Search";
import { ConfirmBlock, ConfirmDelete } from "./Confirmations";
import ArchivedContent from "./ArchivedContent";
import { ContentDetails } from "./ContentDetails/ContentDetails";
import { CourseForm } from "./ContentDetails/components/CourseForm";
import SelectCreator from "./SelectCreator";

type Person = {
  id: string;
  title: string;
  email_id: string;
  date_created: string;
  publisher_data: any;
  last_active_date: string;
  thumb_image: string;
  isActive: boolean;
  course_levels?: any;
  lessons_count: number;
  user_name: string;
};

const columnHelper = createColumnHelper<Person>();

type PublisherType = {
  publisher_id: string;
  full_name: string;
  user_name: string;
  email_id: string;
  user_image: string | null;
  mobile: string;
};

export function Contents() {
  const [sortBy, setSortBy] = useState<"title" | undefined>();
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | undefined>();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = React.useState<any>(false);
  const [selectedCourse, setSelectedCourse] = React.useState<any | null>(null);
  const [search, setSearch] = React.useState("");
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [isBlockedUsers, setIsBlockedUsers] = React.useState(false);
  const [isPublisher, setIsPublisher] = React.useState(false);
  const loadMoreRef = useRef<any>(null);
  const [publisher, setPublisher] = useState<null | PublisherType>(null);
  const [filters, setFilters] = useState<{
    date: { startDate?: string; endDate?: string };
    types?: string[];
    selectedDateFilter?: string;
  }>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isCourseFormOpen, setIsCourseFormOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const storedPublisher = localStorage.getItem("selectedPublisher");
    if (storedPublisher) {
      setPublisher(JSON.parse(storedPublisher));
    } else {
      localStorage.setItem(
        "selectedPublisher",
        JSON.stringify({
          publisher_id: "5cce949b-d547-4929-a894-f172f647757f",
          full_name: "EasyEd",
          user_name: "EasyEd Admin",
          email_id: "admin@yopmail.com",
          user_image: "",
          mobile: "",
          _user_id: "",
        })
      );
    }
  }, [isPublisher]);

  // Cleanup effect to prevent unnecessary API calls when modal is open
  useEffect(() => {
    if (isUserDetailsOpen) {
      // When modal opens, we can optionally invalidate the query to ensure fresh data
      // But we don't want it to keep refetching
      queryClient.setQueryData(["contents", search, filters, sortBy, sortOrder], (oldData: any) => {
        return oldData;
      });
    }
  }, [isUserDetailsOpen, queryClient, search, filters, sortBy, sortOrder]);

  const handleClose = () => {
    setIsUserDetailsOpen(false);
    setSelectedCourse(null);
  };

  const fetchContents = async ({ pageParam }) => {
    const res = await api.getContents({
      page: pageParam,
      limit: 10,
      keyword: search,
      type: "all",
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["contents", search, filters, sortBy, sortOrder],
    queryFn: fetchContents,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      console.log(lastPage, "lastPage");
      if (lastPage?.data?.publisherVideo?.length < 10) return null;
      return pageParam + 1;
    },
    enabled: !isUserDetailsOpen,
    staleTime: 2 * 60 * 1000,
  });

  const columns = [
    columnHelper.accessor("title", {
      header: ({ column }) => {
        return (
          <button
            onClick={() => {
              if (sortBy === "title") {
                setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
              } else {
                setSortBy("title");
                setSortOrder("ASC");
              }
            }}
            className="p-0 flex items-center gap-[2px]"
          >
            Course name
            {sortBy === "title" ? (
              sortOrder === "ASC" ? (
                <ArrowDownIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 rotate-[180deg]" />
              )
            ) : null}
          </button>
        );
      },
      cell: (info) => (
        <div className="flex flex-row items-center gap-2.5">
          <img
            src={info.row.original.thumb_image || UserIcon}
            alt="user"
            className="w-10 h-10 rounded-[3px] opacity-80 object-fill"
          />
          <div className="flex flex-1 flex-col gap-0.5">
            <span>{info.row.original.title}</span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.lessons_count, {
      header: "# of lessons",
    }),
    columnHelper.accessor((row) => row.course_levels, {
      header: "Course level",
    }),
    columnHelper.accessor("date_created", {
      header: "Created on",
      cell: (info) => moment(info.getValue()).format("MMM DD, YYYY") || "-",
    }),
    columnHelper.accessor("publisher_data", {
      header: "Course by",
      cell: (info) => (
        <div className="flex flex-row justify-between w-full items-center">
          <div className="flex flex-row gap-2 items-center">
            <img
              src={
                info.row.original.publisher_data.user_image ||
                UserIcon
              }
              alt="publisher"
              className="w-[35px] h-[35px] rounded object-fill"
            />
            <span className="text-black text-[13px] font-medium">
              {info.row.original.publisher_data.full_name}
            </span>
          </div>
          <UIPopover
            trigger={
              <Button
                variant="outline"
                className="hover:!border-[#9D9D9D] hover:!bg-transparent !border-[#D7D7D7] !rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black"
              >
                <DotsIcon />
              </Button>
            }
          >
            {(close) => (
              <div className="flex flex-col gap-4 bg-white p-4 !pr-8 rounded-lg shadow-[0px_6px_26px_0px_rgba(28,43,40,0.14)] font-inter mr-12 mt-1 text-sm">
                <button
                  className="text-start"
                  onClick={() => {
                    setSelectedCourse(info.row.original);
                    setIsUserDetailsOpen(info.row.original);
                  }}
                >
                  View
                </button>
                <button
                  className="text-start"
                  onClick={() => {
                    setIsBlockModalOpen(true);
                    setSelectedCourse(info.row.original);
                  }}
                >
                  Archive
                </button>
                <button
                  className="text-[#F54343] text-start"
                  onClick={() => {
                    setIsDeleteModalOpen(true);
                    setSelectedCourse(info.row.original);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </UIPopover>
        </div>
      ),
    }),
  ];

  const table = useReactTable<Person>({
    data: tableData || [], // Correctly set the data property
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  useMemo(() => {
    if (data) {
      // Combine pages into one array
      const newData = data.pages.flatMap((page) => page?.data?.publisherVideo);
      // Set the combined data into the table data state
      console.log(newData, "newData");
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage && !isUserDetailsOpen) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isUserDetailsOpen]);

  const hasFilters = useMemo(() => {
    return filters?.date.startDate || (filters?.types?.length || 0) > 0;
  }, [filters]);

  return (
    <>
      <div className="flex flex-col gap-4">
        {!!isUserDetailsOpen && (
          <ContentDetails
            isOpen={!!isUserDetailsOpen}
            onClose={handleClose}
            course={selectedCourse}
            refresh={refetch}
          />
        )}
        {isBlockedUsers && (
          <ArchivedContent
            isOpen={isBlockedUsers}
            handleClose={() => setIsBlockedUsers(false)}
            refetch={refetch}
            onDelete={(course) => {
              setIsDeleteModalOpen(true);
              setSelectedCourse(course);
            }}
          />
        )}
        {isPublisher && (
          <SelectCreator
            isOpen={isPublisher}
            handleClose={() => setIsPublisher(false)}
          />
        )}
        {isDeleteModalOpen && selectedCourse && (
          <ConfirmDelete
            onClose={() => setIsDeleteModalOpen(false)}
            userId={selectedCourse.video_id}
            refetch={() => {
              queryClient.invalidateQueries({ queryKey: ["contents"] });
              queryClient.invalidateQueries({ queryKey: ["archived-content"] });
              refetch();
            }}
            name={`${selectedCourse.title}`}
          />
        )}
        {isBlockModalOpen && selectedCourse && (
          <ConfirmBlock
            onClose={() => setIsBlockModalOpen(false)}
            userId={selectedCourse.video_id}
            refetch={() => {
              queryClient.invalidateQueries({ queryKey: ["contents"] });
              queryClient.invalidateQueries({ queryKey: ["archived-content"] });
            }}
            name={`${selectedCourse.title}`}
          />
        )}

        <div className="flex flex-row justify-between">
          <h1 className="text-black text-2xl font-bold leading-[28.8px]">
            Manage Content
          </h1>
          <div className="flex flex-row justify-between">
            <button
              onClick={() => setIsPublisher(!isPublisher)}
              className="flex items-center gap-2 text-gray-800   rounded-lg hover:bg-gray-100 focus:outline-none mr-10"
            >
              {publisher && (
                <>
                  <img
                    src={
                      publisher.user_image ||
                      UserIcon
                    }
                    alt={publisher.full_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className="text-sm font-medium">
                      Creator : {publisher.full_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {publisher.email_id}
                    </p>
                  </div>
                  <ArrowLeftIcon className="w-4 h-4 [&_path]:stroke-primary" />
                </>
              )}
            </button>
            <Button
              variant="link"
              onClick={() => setIsBlockedUsers(!isBlockedUsers)}
              className="!text-[#393939] !cursor-pointer"
            >
              Archived{" "}
              <ArrowLeftIcon className="w-4 h-4 [&_path]:stroke-primary" />
            </Button>
          </div>
        </div>
        <div className="bg-white p-5 flex flex-col">
          <div className="flex flex-row justify-between items-center mb-5">
            <div className="flex items-center gap-3">
              <SearchComponent
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                placeholder="Search"
                value={search}
              />
            </div>
            <div>
              <Button
                onClick={() => setIsCourseFormOpen(true)}
                variant="primary"
                className="!rounded-full"
              >
                + Add a course
              </Button>
            </div>
          </div>

          <div
            className={`overflow-auto ${
              hasFilters ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-190px)]"
            } relative flex flex-col`}
          >
            <table className="w-full relative">
              <colgroup>
                <col style={{ width: "25%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <thead className="w-full">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="sticky top-0 z-[1]">
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={clsx(
                          "text-[0.875rem] font-medium text-[#202020] text-start p-0"
                        )}
                      >
                        <div
                          className={clsx(
                            "w-full px-3 py-4 bg-[#F2F7F3] flex flex-row items-center gap-1 transition-all",
                            index === headerGroup.headers.length - 1 &&
                              "rounded-tr-[14px] rounded-br-[14px]",
                            index === 0 && "rounded-tl-[14px] rounded-bl-[14px]"
                          )}
                          // onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {/* {{
                          asc: <ArrowIcon />,
                          desc: <ArrowIcon className="rotate-[180deg]" />,
                        }[header.column.getIsSorted() as string] ?? null} */}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {!isLoading &&
                  table.getRowModel().rows.map((row, index) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={clsx(
                            "px-3 py-4 border-b text-black/80 border-b-[#D7D7D7] text-[13px] text-left",
                            // last index no border
                            index === table.getRowModel().rows.length - 1 &&
                              "border-b-0"
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}

                {isFetching && (
                  <TableSkeleton
                    rows={5}
                    columns={table.getAllColumns().length}
                  />
                )}
              </tbody>
            </table>
            {!isLoading && !isFetching && tableData.length === 0 && (
              <div className="flex-1 flex justify-center items-center">
                <span className="text-[#999999] text-[0.875rem] font-medium">
                  No data available
                </span>
              </div>
            )}
            <div className="">
              <button
                onClick={() => fetchNextPage()}
                disabled={!hasNextPage || isFetchingNextPage}
                ref={loadMoreRef}
              >
                {/* {isFetchingNextPage
                ? "Loading more..."
                : hasNextPage
                ? "Load More"
                : "Nothing more to load"} */}
              </button>
            </div>
            {/* <div>{isFetching && !isFetchingNextPage ? "Fetching..." : null}</div> */}
          </div>
        </div>
      </div>
      {isCourseFormOpen && (
        <CourseForm
          onClose={() => setIsCourseFormOpen(false)}
          onCourseCreated={(data) => {
            refetch();
          }}
        />
      )}
    </>
  );
}

export function TableSkeleton({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-5 py-4 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]"
            >
              <div className="w-full h-4 bg-gray-300 rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
